// @flow
import React, { useContext } from 'react';
import { Redirect } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Spinner } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import PrivateRoute from '../PrivateRoute';
import { AppContext } from '../../containers/App/context';

type Props = {
  flagName: string,
  component: any,
  fallback?: React$Node,
  userPermissions?: Array<any>,
  scope?: Array<string>,
  selectedProperty?: any,
  organizationId?: string,
  // Pass `null` while the condition's source data is still loading; a premature `false` redirects
  // to /404 and the user cannot get back by waiting.
  additionalCondition?: boolean | null,
};

// LaunchDarkly bootstraps anonymous and re-identifies with the real user afterwards, so flags read
// as off until `isLdUserContextReady` flips. Deciding before that bounces a flag-on user to /404 on
// every cold load, which is why the route waits rather than trusting the first value it sees.
// `additionalCondition` is waited on the same way: property-derived conditions read false on the
// first render of a cold load, and that is indistinguishable from a real false unless the call site
// says so with `null`.
const FeatureFlagRoute = ({
  flagName,
  component,
  fallback,
  userPermissions,
  scope,
  selectedProperty,
  organizationId,
  additionalCondition = true, // Default to true so it doesn't block if not provided
  ...routeProps
}: Props) => {
  const { isLdUserContextReady } = useContext(AppContext);
  const flags = useFlags();

  if (!isLdUserContextReady || additionalCondition === null) {
    return (
      <PrivateRoute
        {...routeProps}
        component={() =>
          fallback || (
            <div className="loading-container">
              <Spinner />
            </div>
          )
        }
        userPermissions={userPermissions}
        scope={scope}
        selectedProperty={selectedProperty}
        organizationId={organizationId}
      />
    );
  }

  if (flags[flagName] !== true || !additionalCondition) {
    return (
      <PrivateRoute
        {...routeProps}
        component={() => <Redirect to="/404" />}
        userPermissions={userPermissions}
        scope={scope}
        selectedProperty={selectedProperty}
        organizationId={organizationId}
      />
    );
  }

  return (
    <PrivateRoute
      {...routeProps}
      component={component}
      userPermissions={userPermissions}
      scope={scope}
      selectedProperty={selectedProperty}
      organizationId={organizationId}
    />
  );
};

export default FeatureFlagRoute;
